import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  TableContainer as MuiTableContainer,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  Paper,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import OptionsMenuIcon from '../../../assets/icons/OptionMenu';
import { Primary, SemanticError } from '../../color-palette/colors';

export interface Column {
  field: string;
  headerName: string | React.ReactNode;
  renderCell?: (value: any, row: { [key: string]: any }) => React.ReactNode;
  getActions?: (row: { [key: string]: any }, rowIndex: number) => React.ReactNode;
  align?: 'center' | 'left' | 'right' | 'inherit' | 'justify' | undefined;
  style?: any;
  width?: any;
  minWidth?: any;
  maxWidth?: any;
  flex?: number | string;
  sx?: any;
}

interface ReusableTableProps {
  stickyHeader?: boolean;
  columns: Column[];
  rows: { [key: string]: any }[];
  renderOptionsCell?: (row: { [key: string]: any }) => React.ReactNode;
  firstRowRef?: React.RefObject<HTMLTableRowElement>;
  lastRowRef?: React.RefObject<HTMLTableRowElement>;
  selectedRow?: number;
  onRowSelect?: (rowIndex: number) => void;
  scrollbarWidth?: string;
  headerHeight?: string | number;
  tableContainerSx?: any
}

const StyledHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
}));

const StyledBodyCell = styled(MuiTableCell)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
}));

const Table: React.FC<ReusableTableProps> = ({
  columns,
  rows,
  renderOptionsCell,
  stickyHeader,
  firstRowRef,
  lastRowRef,
  onRowSelect,
  selectedRow,
  scrollbarWidth,
  headerHeight,
  tableContainerSx = {} 
}) => {
  function handleRowClick(event: React.MouseEvent, rowIndex: number) {
    event.stopPropagation();
    onRowSelect?.(rowIndex);
  }

  return (
    <MuiTableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${Primary[100]}`,
        borderRadius: '8px',
        boxSizing: 'border-box',
        scrollbarWidth: scrollbarWidth || 'thin',
        '&::-webkit-scrollbar': {
          width: scrollbarWidth || 'thin',
        },
        bgcolor: 'white',
        ...tableContainerSx || {},
      }}
    >
      <MuiTable stickyHeader={stickyHeader}>
        <MuiTableHead>
          <MuiTableRow>
            {columns?.map(column => (
              <StyledHeaderCell
                key={column.field}
                align={column.align || 'left'}
                sx={{
                  width: column.width || 'auto',
                  minWidth: column.minWidth || 'auto',
                  maxWidth: column.maxWidth || 'auto',
                  height: headerHeight || 'auto',
                }}
              >
                <Typography noWrap variant="highlight">
                  {column.headerName}
                </Typography>
              </StyledHeaderCell>
            ))}
            {renderOptionsCell && (
              <StyledHeaderCell key="options" align="right" sx={{ height: headerHeight || 'auto' }}>
                <Typography variant="highlight">
                  <OptionsMenuIcon />
                </Typography>
              </StyledHeaderCell>
            )}
          </MuiTableRow>
        </MuiTableHead>
        <MuiTableBody>
          {rows?.map((row, rowIndex) => (
            <MuiTableRow
              onClick={event => handleRowClick(event, rowIndex)}
              ref={rowIndex === rows?.length - 1 ? lastRowRef : ((rows?.length > 1 && rowIndex === 0) ? firstRowRef : null)}
              key={rowIndex}
              sx={{
                cursor: 'pointer',
                background: row?.isOutDated ? SemanticError[50] : selectedRow === rowIndex ? Primary[50] : '#FFF',
                ':hover': { backgroundColor: Primary[50] },
                borderBottom: rowIndex !== rows.length - 1 ? `1px solid ${Primary[100]}` : 'none',
              }}
            >
              {columns?.map(column => (
                <StyledBodyCell
                  key={column.field}
                  align={column.align || 'left'}
                  sx={{
                    width: column.width || 'auto',
                    minWidth: column.minWidth || 'auto',
                    maxWidth: column.maxWidth || 'auto',
                    borderBottom:
                      rowIndex !== rows.length - 1 ? `1px solid ${Primary[100]}` : 'none',
                    ...(column?.sx || {})
                  }}
                >
                  {column?.renderCell
                    ? column.renderCell(row[column?.field], row)
                    : column.getActions
                      ? column.getActions(row, rowIndex)
                      : row[column.field]}
                </StyledBodyCell>
              ))}
              {renderOptionsCell && (
                <StyledBodyCell
                  align="right"
                  key="options"
                  sx={{
                    borderBottom:
                      rowIndex !== rows.length - 1 ? `1px solid ${Primary[100]}` : 'none',
                  }}
                >
                  {renderOptionsCell(row)}
                </StyledBodyCell>
              )}
            </MuiTableRow>
          ))}
        </MuiTableBody>
      </MuiTable>
    </MuiTableContainer>
  );
};

export default Table;